import Vue from "vue";
import axios from "../plugins/axios";

/**
 * Jurisdiction rules admin store (QUOTE COMP: 12 / S2CWB-237).
 *
 * Every route here is admin-guarded server side and returns 404 while the
 * backend's coverage-comparison flag is off. Responses are camelCase — including
 * the British `favourableLanguage` — and are stored exactly as the API returns
 * them: there is no local rule set and no client-side defaulting of missing
 * fields.
 *
 * Errors are re-thrown so the calling component can surface the API's own
 * message. A failed request never blanks the list it was refreshing.
 */

const RULES_URL = "/admin/jurisdiction-rules";

/** Message an API error carries, or null — never a fabricated default. */
export function apiErrorMessage(error) {
  const message = error?.response?.data?.message;
  return typeof message === "string" && message.trim() ? message : null;
}

const state = () => ({
  rules: [],
  // Every rule an unnarrowed read returned, kept apart from the narrowed
  // `rules` the table shows. The filter's own option lists come from here:
  // derived from the narrowed result, picking NY leaves NY as the only
  // jurisdiction left to pick and the filter erases its own options.
  allRules: [],
  loading: false,
  saving: false,
  error: null,
  filters: {
    jurisdiction: null,
    rowKey: null,
    includeInactive: false,
  },
  panel: {
    open: false,
    mode: null, // "create" | "edit"
    rule: null,
  },
  // Monotonic token for rule reads. Typing fires overlapping requests and they
  // can settle out of order, so only the newest one is allowed to write.
  latestFetchId: 0,
});

const mutations = {
  SET_RULES(state, rules) {
    state.rules = Array.isArray(rules) ? rules : [];
  },

  SET_ALL_RULES(state, rules) {
    state.allRules = Array.isArray(rules) ? rules : [];
  },

  SET_LOADING(state, value) {
    state.loading = Boolean(value);
  },

  SET_SAVING(state, value) {
    state.saving = Boolean(value);
  },

  SET_ERROR(state, message) {
    state.error = message || null;
  },

  SET_FILTERS(state, filters) {
    Object.keys(filters || {}).forEach((key) => {
      Vue.set(state.filters, key, filters[key]);
    });
  },

  // A written rule lands in both lists: the table shows it, and its jurisdiction
  // and row key become filter options without waiting for the next full read.
  UPSERT_RULE(state, rule) {
    if (!rule || !rule.id) return;
    [state.rules, state.allRules].forEach((list) => {
      const index = list.findIndex((r) => r.id === rule.id);
      if (index === -1) {
        list.push(rule);
      } else {
        Vue.set(list, index, rule);
      }
    });
  },

  OPEN_PANEL(state, { mode, rule }) {
    Vue.set(state, "panel", { open: true, mode, rule: rule || null });
  },

  CLOSE_PANEL(state) {
    Vue.set(state, "panel", { open: false, mode: null, rule: null });
  },

  START_FETCH(state) {
    state.latestFetchId += 1;
  },
};

const actions = {
  async fetchRules({ commit, state }, overrides) {
    const filters = { ...state.filters, ...(overrides || {}) };
    const params = {};
    if (filters.jurisdiction) params.jurisdiction = filters.jurisdiction;
    if (filters.rowKey) params.rowKey = filters.rowKey;
    if (filters.includeInactive) params.includeInactive = true;

    commit("START_FETCH");
    const fetchId = state.latestFetchId;
    const isStale = () => state.latestFetchId !== fetchId;

    commit("SET_LOADING", true);
    commit("SET_ERROR", null);
    try {
      const { data } = await axios.get(RULES_URL, { params });
      // A slower earlier request must not overwrite a newer one's result.
      if (isStale()) return data;
      commit("SET_RULES", data?.rules);
      // A read narrowed by neither filter is also the option lists' source.
      if (!params.jurisdiction && !params.rowKey) commit("SET_ALL_RULES", data?.rules);
      return data;
    } catch (error) {
      if (!isStale()) commit("SET_ERROR", apiErrorMessage(error));
      throw error;
    } finally {
      // Only the newest request owns the spinner; an overtaken one leaves it on
      // for the request that is still in flight.
      if (!isStale()) commit("SET_LOADING", false);
    }
  },

  async createRule({ commit }, payload) {
    commit("SET_SAVING", true);
    try {
      const { data } = await axios.post(RULES_URL, payload);
      commit("UPSERT_RULE", data?.rule);
      return data?.rule;
    } finally {
      commit("SET_SAVING", false);
    }
  },

  async updateRule({ commit }, { id, ...payload }) {
    commit("SET_SAVING", true);
    try {
      const { data } = await axios.put(`${RULES_URL}/${id}`, payload);
      commit("UPSERT_RULE", data?.rule);
      return data?.rule;
    } finally {
      commit("SET_SAVING", false);
    }
  },

  /** Deactivation never deletes: the rule stays, with `active` false. */
  async setRuleActive({ commit }, { id, active }) {
    const suffix = active ? "activate" : "deactivate";
    const { data } = await axios.post(`${RULES_URL}/${id}/${suffix}`);
    commit("UPSERT_RULE", data?.rule);
    return data?.rule;
  },

  setFilters({ commit }, filters) {
    commit("SET_FILTERS", filters);
  },

  openPanel({ commit }, { mode, rule }) {
    commit("OPEN_PANEL", { mode, rule });
  },

  closePanel({ commit }) {
    commit("CLOSE_PANEL");
  },
};

const getters = {
  /** Jurisdiction first, then the comparison row — how an SME reads the list. */
  orderedRules: (state) =>
    [...state.rules].sort(
      (a, b) =>
        String(a.jurisdiction || "").localeCompare(String(b.jurisdiction || "")) ||
        String(a.rowKey || "").localeCompare(String(b.rowKey || ""))
    ),

  /**
   * The jurisdictions the catalog holds, for the filter. Read off `allRules`,
   * never the narrowed `rules`: a filter whose options come from its own result
   * offers one value once it is applied and cannot be changed without clearing.
   */
  jurisdictionOptions: (state) => [...new Set(state.allRules.map((rule) => rule.jurisdiction).filter(Boolean))].sort(),

  rowKeyOptions: (state) => [...new Set(state.allRules.map((rule) => rule.rowKey).filter(Boolean))].sort(),
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
